// "I miei design" — cache locale (letta in render, quindi sincrona) più
// sincronizzazione sull'account: i design salvati da chi è loggato vivono nel
// database e si ritrovano da qualsiasi dispositivo. La cache è separata per
// utente: `squiddi_projects_v1` per gli anonimi, `..__<uid>` per chi accede.
import {
  getUserToken, listMyProjects, putMyProject, importMyProjects,
  renameMyProject as apiRenameProject, deleteMyProject as apiDeleteProject,
} from "@/lib/api";

const BASE_KEY = "squiddi_projects_v1";
let ownerId = null;
let syncing = null;

const readKey = (k) => { try { return JSON.parse(localStorage.getItem(k)) || []; } catch { return []; } };
const writeKey = (k, list) => { try { localStorage.setItem(k, JSON.stringify(list)); } catch (e) { /* quota */ } };
const cacheKey = () => (ownerId ? `${BASE_KEY}__${ownerId}` : BASE_KEY);
const online = () => !!ownerId && !!getUserToken();

export function setProjectsOwner(uid) { ownerId = uid || null; }

export function getProjects() { return readKey(cacheKey()); }
function persist(list) { writeKey(cacheKey(), list); }

function markSynced(id) {
  const list = getProjects();
  const idx = list.findIndex((x) => x.id === id);
  if (idx >= 0) { list[idx] = { ...list[idx], synced: true }; persist(list); }
}

export function saveProject(p) {
  const list = getProjects();
  const id = p.id || (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()));
  const rec = { ...p, id, savedAt: new Date().toISOString(), synced: false };
  const idx = list.findIndex((x) => x.id === id);
  if (idx >= 0) list[idx] = rec; else list.unshift(rec);
  persist(list);
  // Copia sull'account in background: se fallisce (offline, design troppo
  // pesante) il record resta `synced: false` e riparte al prossimo sync.
  if (online()) putMyProject(id, rec).then(() => markSynced(id)).catch(() => {});
  return rec;
}

export function deleteProject(id) {
  persist(getProjects().filter((x) => x.id !== id));
  if (online()) apiDeleteProject(id).catch(() => {});
}

export function renameProject(id, name) {
  const list = getProjects();
  const idx = list.findIndex((x) => x.id === id);
  if (idx >= 0) { list[idx] = { ...list[idx], name }; persist(list); }
  if (online()) apiRenameProject(id, name).catch(() => {});
  return list[idx] || null;
}

export function getProject(id) {
  return getProjects().find((x) => x.id === id) || null;
}

async function doSync() {
  // 1. Design creati prima dell'accesso: migrati sull'account una volta sola.
  const anon = readKey(BASE_KEY);
  if (anon.length) {
    try { await importMyProjects(anon); localStorage.removeItem(BASE_KEY); } catch (e) { /* riprova dopo */ }
  }
  // 2. Ripubblica quelli che non sono ancora arrivati sul server.
  for (const p of getProjects().filter((x) => !x.synced)) {
    try { await putMyProject(p.id, p); } catch (e) { /* offline */ }
  }
  // 3. La lista del server è la verità: diventa la nuova cache.
  const server = await listMyProjects();
  const list = server.map((p) => ({ ...p, synced: true }));
  persist(list);
  return list;
}

/** Allinea cache locale e account. Sicuro da chiamare più volte in parallelo. */
export function syncProjects(uid) {
  if (uid !== undefined) setProjectsOwner(uid);
  if (!online()) return Promise.resolve(getProjects());
  if (syncing) return syncing;
  syncing = doSync().finally(() => { syncing = null; });
  return syncing;
}
